import React, { useEffect, useState, useImperativeHandle, forwardRef, useRef } from 'react';
import {
  Box, TextField, Alert, Switch, FormControlLabel,
  Select, MenuItem, FormControl, Divider, Typography, Button, CircularProgress,
  InputBase, InputAdornment, IconButton,
} from '@mui/material';
import {
  SaveOutlined, CameraAltOutlined, AddAPhotoOutlined, CloseOutlined, EditOutlined,
  ChevronLeftOutlined, ChevronRightOutlined, ClearOutlined,
  KeyboardArrowDownOutlined, CheckOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../../hooks/useApi';
import { EVENT_STATUS } from '../../../components/DashboardShared';
import { PRIMARY, SWITCH_SX, INPUT_SX, FormField } from '../shared';
import { ContentBrowserModal } from '../lobbyBuilderTab/ContentBrowserModal';

// ── Banner dimensions — single source of truth ────────────────────────────────
// Change these two values to update the banner everywhere consistently.
const BANNER_W = 1200;
const BANNER_H = 400;
const BANNER_ASPECT_RATIO = BANNER_W / BANNER_H; // 3:1

// ── Section ───────────────────────────────────────────────────────────────────
function Section({ title, children, first = false }) {
  return (
    <Box>
      {!first && <Divider sx={{ mb: 3, borderColor: '#f0f2f5' }} />}
      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', mb: 2.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
      </Box>
    </Box>
  );
}

function Row({ children }) {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {React.Children.map(children, c => <Box sx={{ flex: 1 }}>{c}</Box>)}
    </Box>
  );
}

// ── Card selector ─────────────────────────────────────────────────────────────
function CardSelect({ value, onChange, options }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {options.map(opt => {
        const selected = opt.value === value;
        return (
          <Box
            key={opt.value}
            onClick={() => onChange(opt.value)}
            sx={{
              flex: 1,
              display: 'flex', flexDirection: 'row', alignItems: 'center',
              gap: 1,
              py: 1, px: 1.25,
              borderRadius: 2,
              border: `1.5px solid ${selected ? PRIMARY : '#e4e9f0'}`,
              background: selected ? alpha(PRIMARY, 0.05) : '#fafbfc',
              cursor: 'pointer',
              transition: 'all 0.15s',
              userSelect: 'none',
              '&:hover': {
                borderColor: selected ? PRIMARY : '#c5cdd8',
                background: selected ? alpha(PRIMARY, 0.07) : '#f3f5f8',
              },
            }}
          >
            <Typography sx={{ fontSize: '1rem', lineHeight: 1, flexShrink: 0 }}>{opt.icon}</Typography>
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: selected ? 700 : 600, color: selected ? PRIMARY : '#374151', lineHeight: 1.2 }}>
                {opt.label}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: selected ? alpha(PRIMARY, 0.7) : '#9ea8b3', lineHeight: 1.2 }}>
                {opt.hint}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Editable title ────────────────────────────────────────────────────────────
function EditableTitle({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== value) onChange(draft.trim());
    else setDraft(value);
  };

  if (editing) {
    return (
      <InputBase
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setEditing(false); setDraft(value); }
        }}
        autoFocus
        fullWidth
        sx={{
          fontSize: '1.6rem', fontWeight: 800, color: '#1a1a2e',
          borderBottom: `2px solid ${PRIMARY}`,
          pb: 0.25, mb: 0.5,
          '& input': { p: 0 },
        }}
      />
    );
  }

  return (
    <Box
      onClick={() => setEditing(true)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        cursor: 'text', mb: 0.5,
        '&:hover .edit-icon': { opacity: 1 },
      }}
    >
      <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: '#1a1a2e', lineHeight: 1.2 }}>
        {value || 'Untitled Event'}
      </Typography>
      <EditOutlined className="edit-icon"
        sx={{ fontSize: 16, color: '#9ea8b3', opacity: 0, transition: 'opacity 0.15s', mt: 0.25 }} />
    </Box>
  );
}

// ── Editable slug ─────────────────────────────────────────────────────────────
function EditableSlug({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  const sanitize = v => v.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const commit = () => {
    setEditing(false);
    const clean = sanitize(draft);
    if (clean && clean !== value) onChange(clean);
    else setDraft(value);
  };

  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>/events/</Typography>
        <InputBase
          value={draft}
          onChange={e => setDraft(sanitize(e.target.value))}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setEditing(false); setDraft(value); }
          }}
          autoFocus
          sx={{
            fontSize: '0.82rem', color: PRIMARY,
            borderBottom: `1.5px solid ${PRIMARY}`,
            '& input': { p: 0 },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      onClick={() => setEditing(true)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, mb: 2,
        cursor: 'text', '&:hover .slug-edit': { opacity: 1 },
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>/events/</Typography>
      <Typography variant="caption" sx={{ color: PRIMARY, fontWeight: 600 }}>
        {value || 'untitled'}
      </Typography>
      <EditOutlined className="slug-edit"
        sx={{ fontSize: 11, color: '#9ea8b3', opacity: 0, transition: 'opacity 0.15s' }} />
    </Box>
  );
}

// ── Dual-month date range picker ──────────────────────────────────────────────
const WEEKDAYS_SHORT = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function calendarGrid(year, month) {
  const dowFirst = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < dowFirst; i++) days.push(null);
  const total = new Date(year, month + 1, 0).getDate();
  for (let i = 1; i <= total; i++) days.push(new Date(year, month, i));
  return days;
}

function isoDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const val = `${hh}:${mm}`;
      const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? 'AM' : 'PM';
      opts.push({ val, label: `${hr}:${mm === '00' ? '00' : mm} ${ampm}` });
    }
  }
  return opts;
})();

function TimeDropdown({ value, onChange }) {
  const listRef = React.useRef();

  React.useEffect(() => {
    if (!listRef.current) return;
    const sel = listRef.current.querySelector('[data-selected="true"]');
    if (sel) sel.scrollIntoView({ block: 'center' });
  }, [value]);

  return (
    <Box sx={{
      position: 'absolute', top: '100%', right: 0, zIndex: 1400,
      width: "100%", background: '#fff',
      border: '1px solid #e4e9f0', borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      overflow: 'hidden', mt: 0.5,
    }}>
      <Box ref={listRef} sx={{ maxHeight: 220, overflowY: 'auto', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: '#d0d5dd', borderRadius: 4 } }}>
        {TIME_OPTIONS.map(opt => {
          const selected = opt.val === value;
          return (
            <Box
              key={opt.val}
              data-selected={selected}
              onClick={() => onChange(opt.val)}
              sx={{
                px: 2, py: 0.9, cursor: 'pointer', fontSize: '0.85rem',
                fontWeight: selected ? 700 : 400,
                color: selected ? PRIMARY : '#374151',
                background: selected ? alpha(PRIMARY, 0.08) : 'transparent',
                '&:hover': { background: selected ? alpha(PRIMARY, 0.12) : '#f5f7fa' },
              }}
            >
              {opt.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function EventDateRangePicker({ startDate, endDate, onStartChange, onEndChange, startTime, endTime, onStartTimeChange, onEndTimeChange, singleDay, onSingleDayChange }) {
  const initMonth = () => {
    const d = startDate ? new Date(startDate) : new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  };
  const [view, setView] = React.useState(initMonth);

  React.useEffect(() => {
    if (startDate) {
      const d = new Date(startDate);
      setView({ year: d.getFullYear(), month: d.getMonth() });
    }
  }, [startDate]);

  const [hovered, setHovered] = React.useState(null);
  const [selecting, setSelecting] = React.useState('start');

  const prev = () => {
    if (view.month === 0) setView({ year: view.year - 1, month: 11 });
    else setView(v => ({ ...v, month: v.month - 1 }));
  };
  const next = () => {
    if (view.month === 11) setView({ year: view.year + 1, month: 0 });
    else setView(v => ({ ...v, month: v.month + 1 }));
  };

  const handleDayClick = (d) => {
    const iso = isoDate(d);
    if (singleDay) { onStartChange(iso); onEndChange(''); return; }
    if (selecting === 'start') {
      onStartChange(iso);
      if (endDate && iso > endDate) onEndChange('');
      setSelecting('end');
    } else {
      if (iso < startDate) { onEndChange(startDate); onStartChange(iso); }
      else { onEndChange(iso); }
      setSelecting('start');
    }
  };

  const rangeStart = startDate;
  const rangeEnd = endDate || hovered;

  function MonthGrid({ year, month }) {
    const grid = calendarGrid(year, month);
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a2e' }}>
            {MONTH_NAMES[month]} {year}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.25 }}>
          {WEEKDAYS_SHORT.map(d => (
            <Box key={d} sx={{ textAlign: 'center', py: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#9ea8b3' }}>{d}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid #f0f4f8', borderRadius: 1.5, overflow: 'hidden' }}>
          {grid.map((day, i) => {
            if (!day) return <Box key={`b${i}`} sx={{ height: 36, border: '1px solid #f0f4f8', background: '#fafbfc' }} />;
            const iso = isoDate(day);
            const isStart = iso === rangeStart;
            const isEnd = iso === (endDate || null);
            const isSelected = isStart || isEnd;
            const inRange = rangeStart && rangeEnd && iso > rangeStart && iso < rangeEnd;
            const isToday = iso === isoDate(new Date());

            return (
              <Box
                key={iso}
                onClick={() => handleDayClick(day)}
                onMouseEnter={() => selecting === 'end' && setHovered(iso)}
                onMouseLeave={() => setHovered(null)}
                sx={{
                  height: 36,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', position: 'relative',
                  border: '1px solid #f0f4f8',
                  background: (!singleDay && inRange) ? alpha(PRIMARY, 0.08) : 'transparent',
                  ...(!singleDay && isStart && endDate ? { background: `linear-gradient(to right, transparent 50%, ${alpha(PRIMARY, 0.08)} 50%)` } : {}),
                  ...(!singleDay && isEnd && startDate ? { background: `linear-gradient(to left, transparent 50%, ${alpha(PRIMARY, 0.08)} 50%)` } : {}),
                }}
              >
                <Box sx={{
                  width: 34, height: 34,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%',
                  background: isSelected ? PRIMARY : 'transparent',
                  border: isToday && !isSelected ? `1.5px solid ${PRIMARY}` : 'none',
                  zIndex: 1, transition: 'background 0.12s',
                  '&:hover': { background: isSelected ? '#0d5fa8' : alpha(PRIMARY, 0.12) },
                }}>
                  <Typography sx={{
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? 800 : isToday ? 700 : 400,
                    color: isSelected ? '#fff' : inRange ? PRIMARY : '#374151',
                    lineHeight: 1,
                  }}>
                    {day.getDate()}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  const fmtDisplay = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const fmtTime = (t) => {
    if (!t || typeof t !== 'string' || !t.includes(':')) return '';
    const [h, m] = t.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return '';
    const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hr}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
  };

  const [startTimeOpen, setStartTimeOpen] = React.useState(false);
  const [endTimeOpen, setEndTimeOpen] = React.useState(false);

  React.useEffect(() => {
    const handler = () => { setStartTimeOpen(false); setEndTimeOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <Box sx={{ border: '1px solid #e8edf2', overflow: 'visible' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #f0f4f8' }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>One-day event</Typography>
        <Box
          onClick={() => onSingleDayChange(!singleDay)}
          sx={{
            width: 38, height: 22, borderRadius: 11,
            background: singleDay ? PRIMARY : '#d0d5dd',
            position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
          }}
        >
          <Box sx={{
            position: 'absolute', top: 3,
            left: singleDay ? 'calc(100% - 19px)' : 3,
            width: 16, height: 16, borderRadius: '50%',
            background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
          }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f4f8' }}>
        <Box
          onClick={() => { setSelecting('start'); }}
          sx={{
            flex: 1, px: 2, py: 1.25, cursor: 'pointer',
            borderBottom: selecting === 'start' ? `2px solid ${PRIMARY}` : '2px solid transparent',
            background: selecting === 'start' ? alpha(PRIMARY, 0.03) : 'transparent',
          }}
        >
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: selecting === 'start' ? PRIMARY : '#9ea8b3', mb: 0.25 }}>Start date</Typography>
          <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: startDate ? '#1a1a2e' : '#c0c8d4' }}>{fmtDisplay(startDate) || 'Select date'}</Typography>
        </Box>

        {!singleDay && (
          <Box
            onClick={() => { setSelecting('end'); }}
            sx={{
              flex: 1, px: 2, py: 1.25, cursor: 'pointer',
              borderLeft: '1px solid #f0f4f8',
              borderBottom: selecting === 'end' ? `2px solid ${PRIMARY}` : '2px solid transparent',
              background: selecting === 'end' ? alpha(PRIMARY, 0.03) : 'transparent',
            }}
          >
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: selecting === 'end' ? PRIMARY : '#9ea8b3', mb: 0.25 }}>End date</Typography>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: endDate ? '#1a1a2e' : '#c0c8d4' }}>{fmtDisplay(endDate) || 'Select date'}</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Box onClick={prev} sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1.5, border: '1px solid #e4e9f0', cursor: 'pointer', '&:hover': { background: '#f5f7fa' } }}>
            <ChevronLeftOutlined sx={{ fontSize: 16, color: '#5a6474' }} />
          </Box>
          <Box onClick={next} sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1.5, border: '1px solid #e4e9f0', cursor: 'pointer', '&:hover': { background: '#f5f7fa' } }}>
            <ChevronRightOutlined sx={{ fontSize: 16, color: '#5a6474' }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <MonthGrid year={view.year} month={view.month} />
        </Box>
      </Box>

      <Box sx={{ borderTop: '1px solid #f0f4f8', display: 'flex' }}>
        {/* Start time */}
        <Box onMouseDown={e => e.stopPropagation()} sx={{ flex: 1, position: 'relative' }}>
          <Box
            onClick={() => { setStartTimeOpen(o => !o); setEndTimeOpen(false); }}
            sx={{
              px: 2, py: 1.25, cursor: 'pointer',
              display: 'flex', flexDirection: 'column',
              background: startTimeOpen ? alpha(PRIMARY, 0.04) : 'transparent',
              '&:hover': { background: alpha(PRIMARY, 0.04) },
            }}
          >
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ea8b3', mb: 0.25 }}>Start time</Typography>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: startTime ? '#000' : '#c0c8d4', whiteSpace: 'nowrap' }}>{fmtTime(startTime) || 'Select time'}</Typography>
          </Box>
          {startTimeOpen && <TimeDropdown value={startTime} onChange={v => { onStartTimeChange(v); setStartTimeOpen(false); }} />}
        </Box>

        {/* End time */}
        {!singleDay && (
          <Box onMouseDown={e => e.stopPropagation()} sx={{ flex: 1, position: 'relative', borderLeft: '1px solid #f0f4f8' }}>
            <Box
              onClick={() => { setEndTimeOpen(o => !o); setStartTimeOpen(false); }}
              sx={{
                px: 2, py: 1.25, cursor: 'pointer',
                display: 'flex', flexDirection: 'column',
                background: endTimeOpen ? alpha(PRIMARY, 0.04) : 'transparent',
                '&:hover': { background: alpha(PRIMARY, 0.04) },
              }}
            >
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ea8b3', mb: 0.25 }}>End time</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: endTime ? '#000' : '#c0c8d4', whiteSpace: 'nowrap' }}>{fmtTime(endTime) || 'Select time'}</Typography>
            </Box>
            {endTimeOpen && <TimeDropdown value={endTime} onChange={v => { onEndTimeChange(v); setEndTimeOpen(false); }} />}
          </Box>
        )}
      </Box>

    </Box>
  );
}

// ── Philippine regions ───────────────────────────────────────────────────────

const PH_REGIONS = [
  { code: 'NCR', label: 'NCR – Metro Manila' },
  { code: 'CAR', label: 'CAR – Cordillera' },
  { code: 'I', label: 'Region I – Ilocos' },
  { code: 'II', label: 'Region II – Cagayan Valley' },
  { code: 'III', label: 'Region III – Central Luzon' },
  { code: 'IV-A', label: 'Region IV-A – CALABARZON' },
  { code: 'IV-B', label: 'Region IV-B – MIMAROPA' },
  { code: 'V', label: 'Region V – Bicol' },
  { code: 'VI', label: 'Region VI – Western Visayas' },
  { code: 'VII', label: 'Region VII – Central Visayas' },
  { code: 'VIII', label: 'Region VIII – Eastern Visayas' },
  { code: 'IX', label: 'Region IX – Zamboanga Peninsula' },
  { code: 'X', label: 'Region X – Northern Mindanao' },
  { code: 'XI', label: 'Region XI – Davao' },
  { code: 'XII', label: 'Region XII – SOCCSKSARGEN' },
  { code: 'XIII', label: 'Region XIII – Caraga' },
  { code: 'BARMM', label: 'BARMM – Bangsamoro' },
];

// Cities keyed by region code
const PH_CITIES_BY_REGION = {
  NCR: ['Manila', 'Quezon City', 'Makati', 'Pasig', 'Taguig', 'Mandaluyong', 'Marikina', 'Parañaque', 'Las Piñas', 'Muntinlupa', 'Caloocan', 'Malabon', 'Navotas', 'Valenzuela', 'Pasay', 'Pateros', 'San Juan'],
  CAR: ['Baguio', 'Tabuk', 'Bangued', 'Lagawe', 'Bontoc', 'La Trinidad', 'Kabugao'],
  I: ['Laoag', 'Vigan', 'San Fernando (La Union)', 'Dagupan', 'Alaminos', 'Urdaneta', 'San Carlos (Pangasinan)'],
  II: ['Tuguegarao', 'Cauayan', 'Ilagan', 'Santiago', 'Bayombong', 'Cabarroguis'],
  III: ['San Fernando (Pampanga)', 'Angeles', 'Mabalacat', 'Balanga', 'Meycauayan', 'Malolos', 'San Jose del Monte', 'Cabanatuan', 'Gapan', 'Palayan', 'Tarlac City', 'Olongapo'],
  'IV-A': ['Calamba', 'Santa Rosa', 'Antipolo', 'Biñan', 'Cabuyao', 'San Pedro', 'Lucena', 'Lipa', 'Batangas City', 'Santo Tomas', 'Tagaytay', 'Trece Martires'],
  'IV-B': ['Calapan', 'Puerto Princesa', 'Mamburao', 'San Jose (Occidental Mindoro)', 'Odiongan', 'Romblon'],
  V: ['Legazpi', 'Naga', 'Iriga', 'Masbate City', 'Sorsogon City', 'Tabaco', 'Ligao'],
  VI: ['Iloilo City', 'Bacolod', 'Passi', 'Roxas City', 'Cadiz', 'Sagay', 'San Carlos (Neg. Occ.)', 'Silay', 'Talisay (Neg. Occ.)', 'La Carlota', 'Kabankalan'],
  VII: ['Cebu City', 'Mandaue', 'Lapu-Lapu', 'Talisay (Cebu)', 'Danao', 'Toledo', 'Carcar', 'Bogo', 'Dumaguete', 'Tagbilaran', 'Bais', 'Bayawan', 'Canlaon', 'Guihulngan', 'Tanjay', 'Vigan'],
  VIII: ['Tacloban', 'Ormoc', 'Borongan', 'Calbayog', 'Catbalogan', 'Maasin'],
  IX: ['Zamboanga City', 'Dapitan', 'Dipolog', 'Isabela City', 'Pagadian'],
  X: ['Cagayan de Oro', 'Iligan', 'El Salvador', 'Gingoog', 'Malaybalay', 'Valencia', 'Oroquieta', 'Ozamiz', 'Tangub', 'Marawi'],
  XI: ['Davao City', 'Digos', 'Panabo', 'Samal', 'Tagum', 'Mati'],
  XII: ['General Santos', 'Kidapawan', 'Koronadal', 'Tacurong'],
  XIII: ['Butuan', 'Bayugan', 'Cabadbaran', 'Surigao City', 'Bislig', 'Tandag'],
  BARMM: ['Cotabato City', 'Marawi', 'Lamitan', 'Jolo'],
};

// Flat list for city autocomplete (city + region label pairs)
const ALL_PH_CITIES = Object.entries(PH_CITIES_BY_REGION).flatMap(([region, cities]) =>
  cities.map(city => ({
    city,
    region,
    label: city,
    sublabel: PH_REGIONS.find(r => r.code === region)?.label || region,
  }))
);

// ── CityAutocomplete ──────────────────────────────────────────────────────────

function CityAutocomplete({ value, onChange, region }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Keep query in sync when value changes externally
  useEffect(() => { setQuery(value || ''); }, [value]);

  const pool = region
    ? ALL_PH_CITIES.filter(c => c.region === region)
    : ALL_PH_CITIES;

  const suggestions = query.trim().length === 0
    ? pool.slice(0, 8)
    : pool.filter(c => c.city.toLowerCase().startsWith(query.toLowerCase())).slice(0, 8);

  const handleSelect = (city) => {
    setQuery(city);
    onChange(city);
    setOpen(false);
  };

  const handleBlur = (e) => {
    // Delay so click on list item fires first
    setTimeout(() => {
      if (!listRef.current?.contains(document.activeElement)) {
        setOpen(false);
        // Commit whatever was typed if it doesn't match a suggestion
        onChange(query);
      }
    }, 150);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        value={query}
        placeholder="e.g. Makati"
        onChange={e => { setQuery(e.target.value); setOpen(true); onChange(e.target.value); }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        sx={INPUT_SX}
        InputProps={{
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton size="small" onMouseDown={e => { e.preventDefault(); setQuery(''); onChange(''); setOpen(false); }} sx={{ color: '#b0bac8', p: '2px' }}>
                <ClearOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      {open && suggestions.length > 0 && (
        <Box
          ref={listRef}
          sx={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            zIndex: 1400,
            background: '#fff',
            border: '1px solid #e4e9f0',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            overflow: 'hidden',
          }}
        >
          {suggestions.map((s, i) => (
            <Box
              key={`${s.city}-${s.region}`}
              onMouseDown={e => { e.preventDefault(); handleSelect(s.city); }}
              sx={{
                px: 1.5, py: 0.9,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: i < suggestions.length - 1 ? '1px solid #f4f6f9' : 'none',
                '&:hover': { background: alpha(PRIMARY, 0.05) },
              }}
            >
              <Typography sx={{ fontSize: '0.82rem', color: '#1a1a2e', fontWeight: s.city.toLowerCase() === query.toLowerCase() ? 700 : 400 }}>
                {s.city}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3' }}>
                {s.sublabel.split('–')[1]?.trim() || s.sublabel}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── RegionSelect ──────────────────────────────────────────────────────────────

function RegionSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = PH_REGIONS.find(r => r.code === value);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!btnRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <Box ref={btnRef} sx={{ position: 'relative' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.5, py: '8.5px',
          border: `1px solid ${open ? PRIMARY : '#e4e9f0'}`,
          borderRadius: '9px',
          cursor: 'pointer', background: '#fff',
          transition: 'border-color 0.15s',
          '&:hover': { borderColor: alpha(PRIMARY, 0.5) },
        }}
      >
        <Typography sx={{ fontSize: '0.82rem', color: current ? '#1a1a2e' : '#9ea8b3' }}>
          {current ? current.label : 'Select region'}
        </Typography>
        <KeyboardArrowDownOutlined sx={{
          fontSize: 18, color: '#9ea8b3',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.15s',
        }} />
      </Box>

      {open && (
        <Box sx={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          zIndex: 1400,
          background: '#fff',
          border: '1px solid #e4e9f0',
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          overflow: 'hidden',
          maxHeight: 260, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { background: '#d0d5dd', borderRadius: 4 },
        }}>
          {PH_REGIONS.map((r, i) => {
            const selected = r.code === value;
            return (
              <Box
                key={r.code}
                onMouseDown={e => { e.preventDefault(); onChange(r.code); setOpen(false); }}
                sx={{
                  px: 1.5, py: 0.9,
                  cursor: 'pointer',
                  borderBottom: i < PH_REGIONS.length - 1 ? '1px solid #f4f6f9' : 'none',
                  background: selected ? alpha(PRIMARY, 0.07) : 'transparent',
                  '&:hover': { background: selected ? alpha(PRIMARY, 0.1) : alpha(PRIMARY, 0.04) },
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <Typography sx={{ fontSize: '0.82rem', color: '#1a1a2e', fontWeight: selected ? 700 : 400 }}>
                  {r.label}
                </Typography>
                {selected && <CheckOutlined sx={{ fontSize: 14, color: PRIMARY }} />}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ── Banner + Logo hero ────────────────────────────────────────────────────────
//
// The banner uses a padding-top trick to lock its aspect ratio at any width:
//   paddingTop: `${(BANNER_H / BANNER_W) * 100}%`
// This means the box always occupies exactly BANNER_W × BANNER_H proportions.
//
// The logo is positioned relative to the outer wrapper (not the banner itself)
// so it doesn't affect the banner's height calculation.

const LOGO_SIZE = 88;
const LOGO_OVERLAP = 44;   // how much the logo overlaps the bottom of the banner

function BannerHero({ banner, logo, onBannerChange, onLogoChange }) {
  const [bannerBrowserOpen, setBannerBrowserOpen] = useState(false);
  const [logoBrowserOpen, setLogoBrowserOpen] = useState(false);

  // Track which src is currently being fetched so we can show a spinner
  // until the browser fires onLoad. Reset to false on onLoad / onError.
  const [bannerLoading, setBannerLoading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);

  // When the url changes (new selection), mark as loading immediately
  const prevBanner = React.useRef(banner);
  const prevLogo = React.useRef(logo);
  useEffect(() => {
    if (banner && banner !== prevBanner.current) setBannerLoading(true);
    if (!banner) setBannerLoading(false);
    prevBanner.current = banner;
  }, [banner]);
  useEffect(() => {
    if (logo && logo !== prevLogo.current) setLogoLoading(true);
    if (!logo) setLogoLoading(false);
    prevLogo.current = logo;
  }, [logo]);

  return (
    // Outer wrapper: extra bottom padding carves space for the overlapping logo
    <Box sx={{ mb: `${LOGO_OVERLAP + 16}px`, position: 'relative' }}>

      {/* Banner — aspect-ratio-locked via padding-top trick */}
      <Box sx={{ width: '100%', position: 'relative', paddingTop: `${(BANNER_H / BANNER_W) * 100}%` }}>
        <Box
          onClick={() => setBannerBrowserOpen(true)}
          sx={{
            position: 'absolute', inset: 0,
            background: banner ? 'transparent' : alpha(PRIMARY, 0.06),
            border: banner ? 'none' : `2px dashed ${alpha(PRIMARY, 0.25)}`,
            overflow: 'hidden', cursor: 'pointer',
            '&:hover .banner-overlay': { opacity: !bannerLoading ? 1 : 0 },
          }}
        >
          {banner
            ? <Box component="img" src={banner} alt="banner"
              onLoad={() => setBannerLoading(false)}
              onError={() => setBannerLoading(false)}
              sx={{
                width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                opacity: bannerLoading ? 0 : 1, transition: 'opacity 0.2s ease'
              }} />
            : <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
              <AddAPhotoOutlined sx={{ fontSize: 28, color: alpha(PRIMARY, 0.35) }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: alpha(PRIMARY, 0.5) }}>Upload Banner</Typography>
              <Typography variant="caption" color="text.secondary">{BANNER_W} × {BANNER_H}px recommended</Typography>
            </Box>
          }

          {/* Loading spinner — shown while new banner src is fetching */}
          {bannerLoading && (
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: alpha('#f0f4f8', 0.85),
              backdropFilter: 'blur(4px)',
            }}>
              <CircularProgress size={28} sx={{ color: PRIMARY }} />
            </Box>
          )}

          <Box className="banner-overlay" sx={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0, transition: 'opacity 0.18s ease',
          }}>
            <CameraAltOutlined sx={{ color: '#fff', fontSize: 30 }} />
          </Box>
          {banner && !bannerLoading && (
            <Box
              onClick={e => { e.stopPropagation(); onBannerChange(''); }}
              sx={{
                position: 'absolute', top: 8, right: 8,
                width: 24, height: 24, borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <CloseOutlined sx={{ fontSize: 14, color: '#fff' }} />
            </Box>
          )}
        </Box>

        {/* Logo — anchored to bottom-left of the banner box, overlapping downward */}
        <Box
          onClick={() => setLogoBrowserOpen(true)}
          sx={{
            position: 'absolute', bottom: -LOGO_OVERLAP, left: 24,
            width: LOGO_SIZE, height: LOGO_SIZE, borderRadius: 2,
            border: '3px solid #fff',
            background: '#f4f7fa',
            overflow: 'hidden', cursor: 'pointer',
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover .logo-overlay': { opacity: !logoLoading ? 1 : 0 },
          }}
        >
          {logo
            ? <Box component="img" src={logo} alt="logo"
              onLoad={() => setLogoLoading(false)}
              onError={() => setLogoLoading(false)}
              sx={{
                width: '100%', height: '100%', objectFit: 'cover',
                opacity: logoLoading ? 0 : 1, transition: 'opacity 0.2s ease'
              }} />
            : <AddAPhotoOutlined sx={{ fontSize: 22, color: alpha(PRIMARY, 0.45) }} />
          }

          {/* Loading spinner — shown while new logo src is fetching */}
          {logoLoading && (
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: alpha('#f0f4f8', 0.9),
            }}>
              <CircularProgress size={18} sx={{ color: PRIMARY }} />
            </Box>
          )}

          <Box className="logo-overlay" sx={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.38)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0, transition: 'opacity 0.18s ease',
          }}>
            <CameraAltOutlined sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
        </Box>
      </Box>

      {/* Banner browser — forces the banner aspect ratio in the crop editor */}
      <ContentBrowserModal
        open={bannerBrowserOpen}
        onClose={() => setBannerBrowserOpen(false)}
        onSelect={url => { onBannerChange(url); setBannerBrowserOpen(false); }}
        mediaType="image"
        currentUrl={banner || ''}
        forcedAspectRatio={BANNER_ASPECT_RATIO}
      />

      {/* Logo browser — free crop (no forced ratio) */}
      <ContentBrowserModal
        open={logoBrowserOpen}
        onClose={() => setLogoBrowserOpen(false)}
        onSelect={url => { onLogoChange(url); setLogoBrowserOpen(false); }}
        mediaType="image"
        currentUrl={logo || ''}
      />
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const EventInfoTab = forwardRef(function EventInfoTab({ event, onSave }, ref) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Whether the form has been initialised from the event prop at least once.
  // We skip the auto-save effect until this is true so we don't fire on mount.
  const initialised = useRef(false);
  const autoSaveTimer = useRef(null);

  useEffect(() => {
    if (!event) return;
    initialised.current = false; // reset so next setForm doesn't trigger auto-save
    setForm({
      name: event.name || '',
      slug: event.slug || '',
      logoUrl: event.branding?.logoUrl || event.logoUrl || '',
      coverImage: event.coverImage || '',
      bannerUrl: event.branding?.bannerUrl || event.bannerUrl || '',
      startDate: event.startDate ? event.startDate.split('T')[0] : '',
      endDate: event.endDate ? event.endDate.split('T')[0] : '',
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      isAllDay: !!event.isAllDay,
      locationType: event.locationType || 'virtual',
      venue: event.venue || '',
      region: event.address?.region || 'NCR',
      city: event.address?.city || '',
      maxCapacity: event.maxCapacity || '',
      status: event.status || 'draft',
      isPublic: event.isPublic !== false,
      requiresApproval: !!event.requiresApproval,
      loginEnabled: event.login?.loginEnabled !== false,
      loginMethod: event.login?.method || 'email',
      maxDevices: event.login?.maxDevices || '',
    });
    // Mark as initialised on the next tick (after setForm has run)
    setTimeout(() => { initialised.current = true; }, 0);
  }, [event]);

  const buildPayload = (f) => ({
    name: f.name,
    slug: f.slug,
    coverImage: f.coverImage,
    branding: {
      ...(event?.branding || {}),
      logoUrl: f.logoUrl,
      bannerUrl: f.bannerUrl,
    },
    startDate: f.startDate,
    endDate: f.endDate || null,
    startTime: f.startTime,
    endTime: f.endTime,
    isAllDay: f.isAllDay,
    locationType: f.locationType,
    venue: f.venue,
    address: { region: f.region || 'NCR', city: f.city },
    maxCapacity: f.maxCapacity || null,
    status: f.status,
    isPublic: f.isPublic,
    requiresApproval: f.requiresApproval,
    login: {
      loginEnabled: f.loginEnabled,
      method: f.loginMethod,
      maxDevices: f.maxDevices || null,
    },
  });

  const doSave = async (currentForm) => {
    if (!event?._id) return;
    setSaving(true); setSaveError('');
    try {
      await api.put(`/events/${event._id}`, buildPayload(currentForm));
      setSaved(true); onSave?.();
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setSaveError(e?.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  // Expose manual save via ref (used by parent tabs that call ref.save())
  const handleSave = () => doSave(form);

  // Auto-save: debounce 1 s after any form change, skip the initial hydration
  useEffect(() => {
    if (!initialised.current) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => doSave(form), 1000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [form]); // eslint-disable-line

  useImperativeHandle(ref, () => ({
    save: handleSave,
    get saving() { return saving; },
    get saved() { return saved; },
  }), [handleSave, saving, saved]);

  const showAddress = form.locationType === 'physical' || form.locationType === 'hybrid';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: 6 }}>

      {/* ── Banner + Logo ── */}
      <BannerHero
        banner={form.coverImage || form.bannerUrl || ''}
        logo={form.logoUrl || ''}
        onBannerChange={v => { setForm(p => ({ ...p, coverImage: v, bannerUrl: v })); }}
        onLogoChange={v => setForm(p => ({ ...p, logoUrl: v }))}
      />

      {/* ── Editable title + slug ── */}
      <Box>
        <EditableTitle value={form.name || ''} onChange={v => setForm(p => ({ ...p, name: v }))} />
        <EditableSlug value={form.slug || ''} onChange={v => setForm(p => ({ ...p, slug: v }))} />
      </Box>


      {/* ── Schedule + Location ── */}
      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>

        <Box sx={{ flex: 1 }}>
          <Section title="Schedule" first>
            <EventDateRangePicker
              startDate={form.startDate}
              endDate={form.endDate}
              onStartChange={v => setForm(p => ({ ...p, startDate: v }))}
              onEndChange={v => setForm(p => ({ ...p, endDate: v }))}
              startTime={form.startTime}
              endTime={form.endTime}
              onStartTimeChange={v => setForm(p => ({ ...p, startTime: v }))}
              onEndTimeChange={v => setForm(p => ({ ...p, endTime: v }))}
              singleDay={!!form.isAllDay}
              onSingleDayChange={v => setForm(p => ({ ...p, isAllDay: v, endDate: v ? '' : p.endDate }))}
            />
          </Section>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>

          <Section title="Location" first>
            <FormField label="Location Type">
              <CardSelect
                value={form.locationType || 'virtual'}
                onChange={v => setForm(p => ({ ...p, locationType: v }))}
                options={[
                  { value: 'virtual', label: 'Virtual', icon: '🌐', hint: 'Online only' },
                  { value: 'physical', label: 'In-Person', icon: '📍', hint: 'Physical venue' },
                  { value: 'hybrid', label: 'Hybrid', icon: '🔀', hint: 'Both' },
                ]}
              />
            </FormField>
            {showAddress && (
              <>
                <FormField label="Venue Name">
                  <TextField value={form.venue || ''} onChange={e => setForm(p => ({ ...p, venue: e.target.value }))}
                    fullWidth size="small" placeholder="SMX Convention Center" sx={INPUT_SX} />
                </FormField>
                <Row>
                  <FormField label="Region">
                    <RegionSelect
                      value={form.region || 'NCR'}
                      onChange={v => setForm(p => ({ ...p, region: v, city: '' }))}
                    />
                  </FormField>
                  <FormField label="City">
                    <CityAutocomplete
                      value={form.city || ''}
                      onChange={v => setForm(p => ({ ...p, city: v }))}
                      region={form.region || 'NCR'}
                    />
                  </FormField>
                </Row>
              </>
            )}
          </Section>

          <Section title="Status & Visibility">
            <FormField label="Status">
              <CardSelect
                value={form.status || 'draft'}
                onChange={v => setForm(p => ({ ...p, status: v }))}
                options={[
                  { value: 'draft', label: 'Draft', icon: '📝', hint: 'Not yet visible' },
                  { value: 'published', label: 'Published', icon: '🚀', hint: 'Live to attendees' },
                ]}
              />
            </FormField>
          </Section>

          <Section title="Attendee Login">
            <FormField label="Login Method">
              <CardSelect
                value={form.loginMethod || 'email'}
                onChange={v => setForm(p => ({ ...p, loginMethod: v }))}
                options={[
                  { value: 'email', label: 'Email', icon: '📧', hint: 'Email + password' },
                  { value: 'employeeNumber', label: 'Employee No.', icon: '🪪', hint: 'Staff ID login' },
                  { value: 'magicLink', label: 'Magic Link', icon: '🔗', hint: 'Passwordless' },
                ]}
              />
            </FormField>
            <FormField label="Max Devices" hint="Concurrent sessions per attendee · blank = unlimited">
              <TextField
                type="number"
                value={form.maxDevices || ''}
                onChange={e => setForm(p => ({ ...p, maxDevices: e.target.value }))}
                fullWidth size="small" placeholder="Unlimited" sx={INPUT_SX}
              />
            </FormField>
          </Section>

        </Box>
      </Box>
    </Box>
  );
});

export default EventInfoTab;